import type { LocalGuest } from "./localGuest";

export const SERVER_ACCOUNT_STORAGE_KEY = "bdc.clubhouse.server.v1";

export type ServerAccount = {
  id: string;
  accountKind: "guest" | "persistent" | "system";
  localGuestId: string | null;
  email: string | null;
  handle: string;
  memberNo: number | null;
  isTest: boolean;
};

type AccountResponse = {
  ok: boolean;
  account: ServerAccount;
  recoveryCode?: string;
  message?: string;
  error?: string;
};

export class AccountApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code = "account_error",
  ) {
    super(message);
  }
}

function validAccount(value: unknown): value is ServerAccount {
  if (!value || typeof value !== "object") return false;
  const account = value as Partial<ServerAccount>;
  return typeof account.id === "string"
    && (account.accountKind === "guest" || account.accountKind === "persistent" || account.accountKind === "system")
    && typeof account.handle === "string"
    && (account.memberNo === null || Number.isSafeInteger(account.memberNo));
}

function remember(account: ServerAccount): ServerAccount {
  try {
    window.localStorage.setItem(SERVER_ACCOUNT_STORAGE_KEY, JSON.stringify(account));
  } catch {
    // The HttpOnly server session remains authoritative if local storage is unavailable.
  }
  return account;
}

function safeStorage(): Storage | null {
  // Accessing window.localStorage itself can throw in storage-denied modes;
  // evaluating it as a default parameter would crash the caller (W4 P1-7).
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readServerAccount(storage: Storage | null = safeStorage()): ServerAccount | null {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(SERVER_ACCOUNT_STORAGE_KEY) ?? "null");
    return validAccount(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

async function accountRequest(path: string, init: RequestInit = {}): Promise<AccountResponse> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json", ...init.headers },
  });
  const payload = await response.json() as Partial<AccountResponse>;
  if (!response.ok || !payload.account || !validAccount(payload.account)) {
    throw new AccountApiError(
      response.status,
      payload.message ?? payload.error ?? "The membership desk did not answer.",
      payload.error,
    );
  }
  return payload as AccountResponse;
}

export async function currentServerAccount(): Promise<ServerAccount | null> {
  const response = await fetch("/api/auth/status", {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  const payload = await response.json() as {
    ok?: boolean;
    account?: unknown;
    message?: string;
    error?: string;
  };
  if (!response.ok || !payload.ok) {
    throw new AccountApiError(
      response.status,
      payload.message ?? payload.error ?? "The membership desk did not answer.",
      payload.error,
    );
  }
  if (validAccount(payload.account)) return remember(payload.account);
  try {
    window.localStorage.removeItem(SERVER_ACCOUNT_STORAGE_KEY);
  } catch {
    // A stale local display hint is harmless when storage is unavailable.
  }
  return null;
}

export async function ensureServerGuest(guest: LocalGuest): Promise<ServerAccount> {
  const current = await currentServerAccount();
  if (current) return current;
  const result = await accountRequest("/api/auth/guest", {
    method: "POST",
    body: JSON.stringify({ localGuestId: guest.id }),
  });
  return remember(result.account);
}

export async function signupServerAccount(
  username: string,
  password: string,
): Promise<{ account: ServerAccount; recoveryCode: string }> {
  const result = await accountRequest("/api/auth/signup", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
  if (!result.recoveryCode) throw new AccountApiError(500, "The recovery code was not issued.");
  return { account: remember(result.account), recoveryCode: result.recoveryCode };
}

export async function loginServerAccount(username: string, password: string): Promise<ServerAccount> {
  const result = await accountRequest("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
  return remember(result.account);
}

export async function completeTikTokSignup(username: string): Promise<ServerAccount> {
  const result = await accountRequest("/api/auth/tiktok/complete", {
    method: "POST",
    body: JSON.stringify({ username }),
  });
  return remember(result.account);
}

export async function upgradeServerGuest(handle: string): Promise<{ account: ServerAccount; recoveryCode: string }> {
  const result = await accountRequest("/api/auth/upgrade", {
    method: "POST",
    body: JSON.stringify({ handle }),
  });
  if (!result.recoveryCode) throw new AccountApiError(500, "The recovery code was not issued.");
  return { account: remember(result.account), recoveryCode: result.recoveryCode };
}

export async function recoverServerAccount(
  code: string,
): Promise<{ account: ServerAccount; recoveryCode?: string }> {
  const result = await accountRequest("/api/auth/recover", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
  // A used recovery code is burned server-side; the reissued replacement
  // rides back on the same response and MUST be shown to the player.
  return { account: remember(result.account), recoveryCode: result.recoveryCode };
}

export async function rotateRecoveryCode(): Promise<{ account: ServerAccount; recoveryCode: string }> {
  const result = await accountRequest("/api/auth/recovery/rotate", { method: "POST" });
  if (!result.recoveryCode) throw new AccountApiError(500, "The new recovery code was not issued.");
  return { account: remember(result.account), recoveryCode: result.recoveryCode };
}

export async function logoutServerAccount(): Promise<void> {
  const response = await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  const payload = await response.json() as { ok?: boolean; message?: string; error?: string };
  if (!response.ok || !payload.ok) {
    throw new AccountApiError(
      response.status,
      payload.message ?? payload.error ?? "Sign out did not finish.",
      payload.error,
    );
  }
  try {
    window.localStorage.removeItem(SERVER_ACCOUNT_STORAGE_KEY);
  } catch {
    // The server cookie is authoritative; private browsing can deny local storage.
  }
}
